import config from '@conf'
import { xdr, ReviewRequestBuilder } from 'tokend-js-sdk'
import { Service } from './service'
import request from '../builders/request_builder/index'
import { initService } from './init.service'
import { WITHDRAWAL_TYPE_EMPTY } from '@const'

export class ReviewableRequestsService extends Service {
  /**
   * Loads all token creation reviewable requests for current user
   *
   * @return {Promise<object>} - Promise object representing rr
   */
  loadTokensReviewableRequests () {
    return this._horizonRequestBuilder
      .reviewableRequestsHelper()
      .assets()
      .forRequestor(this._accountId)
      .order('desc')
      .limit(config.TRANSACTIONS_PER_PAGE)
      .callWithSignature(this._keypair)
  }

  /**
   * Loads all withdrawal reviewable requests for current user
   *
   * @return {Promise<object>} - Promise object representing rr
   */
  loadWithdrawalsReviewableRequests () {
    return this._horizonRequestBuilder
      .reviewableRequestsHelper()
      .withdrawals()
      .forRequestor(this._accountId)
      .order('desc')
      .limit(config.TRANSACTIONS_PER_PAGE)
      .callWithSignature(this._keypair)
  }

  /**
   * Loads withdrawal reviewable requests for filters
   *
   * @return {Promise<object>} - Promise object representing rr
   */
  async loadWithdrawalsRequests ({asset,
                            state,
                            requestor,
                            updated_after,
                            created: {from, to},
                            restrict: {notaid, withdrawable, cashable},
                            cursor,
                            withdrawal_type}) {
    let valid = (asset && asset.length > 0)
    if (!valid) valid = (state && state > 0)
    if (!valid) valid = (requestor && requestor.length > 0)
    if (!valid) valid = ((from && from.length > 0) || (to && to.length > 0))
    if (!valid) return []

    const req = request.withdawals()
      .forWithdrawalType(WITHDRAWAL_TYPE_EMPTY)
      .forDestAsset(asset)
      .forState(state)
      .forRequestor(requestor)
      .forTimeRange({from: from, to: to})
      .restrict({notaid: notaid, withdrawable: withdrawable, cashable: cashable})
      .forPage({order: 'desc', by: 'created_at', limit: config.TRANSACTIONS_PER_PAGE})
      .cursor(cursor)

    let builder = this._horizonRequestBuilder
      .reviewableRequestsHelper()
      .withdrawals()

    req.query.forEach(query => {
      builder.url.addQuery(query.key, query.value)
    })
    return builder.callWithSignature(this._keypair)
  }

 /**
   * Loads all update kyc reviewable requests for current user
   *
   * @return {Promise<object>} - Promise object representing rr
   */
  loadKycReviewableRequests (accountId = this._accountId) {
    return this._horizonRequestBuilder
      .reviewableRequestsHelper()
      .update_kyc()
      .forRequestor(accountId)
      .order('desc')
      .limit(config.TRANSACTIONS_PER_PAGE)
      .callWithSignature(this._keypair)
  }

   /**
   * Loads all preissuance upload requests for current user
   *
   * @return {Promise<object>} - Promise object representing rr
   */
  loadPreIssuanceRequests () {
    return this._horizonRequestBuilder
      .reviewableRequestsHelper()
      .preissuances()
      .forRequestor(this._accountId)
      .order('desc')
      .limit(config.TRANSACTIONS_PER_PAGE)
      .callWithSignature(this._keypair)
  }

  /**
   * Load token creation reviewable request by id
   *
   * @param {string} id - id of reviewable requests
   * @return {Promise<Object>}
   */
  loadReviewableRequestById (id) {
    return this._horizonRequestBuilder.reviewableRequestsHelper()
      .assets()
      .reviewableRequest(id)
      .callWithSignature(this._keypair)
  }

  /**
   * Loads all sales rr for corresponding token code
   * @param {string} code - token code
   * @return {Promise<Object>}
   */
  loadSalesReviewableRequestByTokenCode (code) {
    return this._horizonRequestBuilder.reviewableRequestsHelper()
      .sales()
      .forBaseAsset(code)
      .forRequestor(this._accountId)
      .callWithSignature(this._keypair)
      .then(response => response.records[0])
  }

  /**
   * Loads all contract signature reviewable requests for current user
   *
   * @return {Promise<object>} - Promise object representing rr
   */
  loadContractsReviewableRequests (filters = {}, accountId = this._accountId) {
    return this._horizonRequestBuilder
      .reviewableRequestsHelper()
      .contracts()
      .forRequestor(accountId)
      .forReviewer(accountId)
      .forContractNumber(filters.contractNumber)
      .forStartTime(filters.startTime)
      .forEndTime(filters.endTime)
      .order('desc')
      .limit(config.TRANSACTIONS_PER_PAGE)
      .callWithSignature(this._keypair)
  }

  loadInvoicesReviewableRequests (contractId) {
    return this._horizonRequestBuilder
      .reviewableRequestsHelper()
      .invoices()
      .forContractID(contractId)
      .order('desc')
      .limit(config.TRANSACTIONS_PER_PAGE)
      .callWithSignature(this._keypair)
  }

  loadLimitsUpdateReviewableRequests () {
    return this._horizonRequestBuilder
      .reviewableRequestsHelper()
      .limits_updates()
      .forRequestor(this._accountId)
      .order('desc')
      .limit(config.TRANSACTIONS_PER_PAGE)
      .callWithSignature(this._keypair)
  }

  async reviewWithdraw ({ reason, isPermanent = false }, opts) {
    const config = await initService.getServerConfig()
    if (config) {
      const masterAccountId = config._parsedData.master_account_id
      const action = reason && reason.length ? (isPermanent
        ? xdr.ReviewRequestOpAction.permanentReject().value
        : xdr.ReviewRequestOpAction.reject().value)
        : xdr.ReviewRequestOpAction.approve().value

      const operation = ReviewRequestBuilder.reviewWithdrawRequest({
        requestID: opts.id,
        requestHash: opts.requestHash,
        requestType: opts.request_type_i || opts.requestTypeI,
        source: masterAccountId,
        action,
        reason,
        externalDetails: {}
      })

      return this._operationBuilder
        .operation()
        .add(operation)
        .submit(this._accountId, this._keypair)
    }
  }
}

export const reviewableRequestsService = new ReviewableRequestsService()



// WEBPACK FOOTER //
// ./src/js/services/reviewable_requests.service.js